import { useState, useRef, useEffect, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useMyWallets, usePaymentPlans, useWalletInvitationsPending, QUERY_KEYS } from "@/hooks/use-queries";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { OnboardingChecklist } from "@/components/onboarding-checklist";
import { useAnimatedCounter } from "@/hooks/use-animated-counter";
import { useConfetti } from "@/components/confetti-burst";
import {
  Wallet,
  Heart,
  HeartPulse,
  Users,
  TrendingUp,
  Plus,
  ArrowRight,
  DollarSign,
  Building2,
  Search,
  Eye,
  Compass,
  Clock,
  ArrowUpCircle,
  ArrowDownCircle,
  Settings,
  Share2,
  CreditCard,
  CalendarClock,
  FileText,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { PageAbout } from "@/components/page-about";
import { CommunityImpactSection } from "@/components/community-impact";
import { SmartCTAs } from "@/components/smart-ctas";
import { ReceivedThankYous } from "@/components/thank-you-flow";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface WalletData {
  id: string;
  userId: string;
  patientName: string;
  category: string;
  description?: string;
  balance: number;
  totalContributions: number;
  totalSpent: number;
  goalAmount?: number;
  isPublic: boolean;
  shareToken: string;
  createdAt: string;
  walletType?: string;
  organizationName?: string;
}

interface Transaction {
  id: number;
  walletId: string;
  type: string;
  amount: number;
  description?: string;
  contributorName?: string;
  orderId?: string;
  status: string;
  createdAt: string;
}

interface WalletInvitation {
  id: string;
  walletId: string;
  inviterName?: string;
  role: string;
  status: string;
  createdAt: string;
  wallet?: WalletData;
}

interface PaymentPlan {
  id: string;
  walletId: string;
  hospitalName: string;
  originalBalance: number;
  remainingBalance: number;
  totalPaid: number;
  paymentAmount: number;
  frequency: string;
  status: string;
  isPaused: boolean;
  nextPaymentDate?: string;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function getWalletTypeIcon(type?: string) {
  switch (type) {
    case "family":
      return <Users className="h-5 w-5" />;
    case "community":
      return <Heart className="h-5 w-5" />;
    case "organization":
      return <Building2 className="h-5 w-5" />;
    default:
      return <Wallet className="h-5 w-5" />;
  }
}

function getWalletTypeKey(type?: string) {
  switch (type) {
    case "individual":
      return "walletHub.walletType.individual";
    case "family":
      return "walletHub.walletType.family";
    case "community":
      return "walletHub.walletType.community";
    case "condition":
      return "walletHub.walletType.condition";
    case "organization":
      return "walletHub.walletType.organization";
    default:
      return "walletHub.walletType.fallback";
  }
}

function getWalletTypeBadgeVariant(type?: string): "default" | "secondary" | "outline" {
  switch (type) {
    case "family":
      return "secondary";
    case "community":
      return "outline";
    case "organization":
      return "default";
    default:
      return "secondary";
  }
}

function MobileCardDeck({ children, sectionLabels }: { children: React.ReactNode[]; sectionLabels: string[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const getSlidePositions = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return [];
    const slides = Array.from(el.children) as HTMLElement[];
    return slides.map(slide => slide.offsetLeft);
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const positions = getSlidePositions();
    if (positions.length === 0) return;
    let closest = 0;
    let minDist = Math.abs(scrollLeft - positions[0]);
    for (let i = 1; i < positions.length; i++) {
      const dist = Math.abs(scrollLeft - positions[i]);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    }
    setActiveIndex(closest);
  }, [getSlidePositions]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollToIndex = (index: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const positions = getSlidePositions();
    if (positions[index] !== undefined) {
      el.scrollTo({ left: positions[index], behavior: "smooth" });
    }
  };

  return (
    <div data-testid="mobile-card-deck-wrapper">
      <div ref={scrollRef} className="mobile-card-deck" data-testid="mobile-card-deck">
        {children.map((child, i) => (
          <div key={sectionLabels[i] || i} className="mobile-card-slide">
            {child}
          </div>
        ))}
      </div>
      {children.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="deck-dots">
          {sectionLabels.map((label, i) => (
            <button
              key={label}
              onClick={() => scrollToIndex(i)}
              aria-label={label}
              className={`section-dot bg-muted-foreground/30 ${i === activeIndex ? "section-dot-active bg-primary" : ""}`}
              data-testid={`deck-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function WalletHubPage() {
  usePageTitle("My Health Wallet", "Your personal HealthWallet hub for managing wallets, contributions, payment schedules, and community fund memberships.");
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [isLinkEmployerOpen, setIsLinkEmployerOpen] = useState(false);
  const [companyCode, setCompanyCode] = useState("");

  const { data: allWallets = [], isLoading: walletsLoading } = useMyWallets();

  const { data: recentTransactions = [], isLoading: transactionsLoading } = useQuery<Transaction[]>({
    queryKey: ["/api/my-wallets/transactions?limit=50"],
    enabled: allWallets.length > 0,
  });

  const { data: pendingInvitations = [] } = useWalletInvitationsPending();

  const { data: paymentPlans = [] } = usePaymentPlans();

  const { data: employerEnrollment } = useQuery<any>({
    queryKey: ["/api/employer/my-enrollment"],
  });

  const { data: pendingThankYouData } = useQuery<{ count: number }>({
    queryKey: ["/api/thank-yous/pending/count"],
  });

  const { data: conditionMemberships = [], isLoading: membershipsLoading } = useQuery<any[]>({
    queryKey: ["/api/user/condition-memberships"],
  });

  const linkEmployerMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await apiRequest("POST", "/api/employer/link", { companyCode: code });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/employer/my-enrollment"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myWallets });
      setIsLinkEmployerOpen(false);
      setCompanyCode("");
      toast({
        title: "Employer Linked",
        description: "Your wallet is now connected to your employer's health benefit program.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to link employer",
        variant: "destructive",
      });
    },
  });

  const acceptInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const response = await apiRequest("POST", `/api/wallet-invitations/${invitationId}/accept`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.walletInvitationsPending });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myWallets });
      toast({
        title: "Invitation Accepted",
        description: "You now have access to the wallet.",
      });
    },
  });

  const declineInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const response = await apiRequest("POST", `/api/wallet-invitations/${invitationId}/decline`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.walletInvitationsPending });
      toast({
        title: "Invitation Declined",
        description: "The invitation has been declined.",
      });
    },
  });

  const [hardshipDialogOpen, setHardshipDialogOpen] = useState(false);
  const [hardshipForm, setHardshipForm] = useState({
    reason: "",
    amountRequested: "",
    supportingDetails: "",
    walletId: "",
  });

  const { data: myHardshipRequests = [] } = useQuery<any[]>({
    queryKey: QUERY_KEYS.myHardshipAssistanceRequests,
  });

  const submitHardshipMutation = useMutation({
    mutationFn: async () => {
      const amountCents = Math.round(parseFloat(hardshipForm.amountRequested || "0") * 100);
      const response = await apiRequest("POST", "/api/hardship-assistance", {
        reason: hardshipForm.reason,
        amountRequestedCents: amountCents,
        supportingDetails: hardshipForm.supportingDetails || undefined,
        walletId: hardshipForm.walletId || undefined,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myHardshipAssistanceRequests });
      setHardshipDialogOpen(false);
      setHardshipForm({ reason: "", amountRequested: "", supportingDetails: "", walletId: "" });
      toast({ title: "Request Submitted", description: "Your hardship assistance request has been received." });
    },
    onError: () => {
      toast({ title: "Submission Failed", description: "Could not submit your request. Please try again.", variant: "destructive" });
    },
  });

  const totalBalance = allWallets.reduce((sum, w) => sum + (w.balance || 0), 0);
  const totalContributions = allWallets.reduce((sum, w) => sum + (w.totalContributions || 0), 0);
  const totalSpent = allWallets.reduce((sum, w) => sum + (w.totalSpent || 0), 0);

  const animatedBalance = useAnimatedCounter(totalBalance / 100, { duration: 900 });
  const animatedContributions = useAnimatedCounter(totalContributions / 100, { duration: 900, delay: 100 });
  const animatedSpent = useAnimatedCounter(totalSpent / 100, { duration: 900, delay: 200 });
  const { fire: fireConfetti, ConfettiCanvas } = useConfetti();

  const prevBalanceRef = useRef(totalBalance);
  const balanceCardRef = useRef<HTMLDivElement>(null);
  const mobileBalanceCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (prevBalanceRef.current !== totalBalance && totalBalance > prevBalanceRef.current && prevBalanceRef.current > 0) {
      [balanceCardRef, mobileBalanceCardRef].forEach(ref => {
        ref.current?.classList.remove("balance-pop");
        void ref.current?.offsetWidth;
        ref.current?.classList.add("balance-pop");
      });
    }
    prevBalanceRef.current = totalBalance;
  }, [totalBalance]);

  const prevMilestoneRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    allWallets.forEach(w => {
      if (w.goalAmount && w.goalAmount > 0) {
        const progress = (w.totalContributions / w.goalAmount) * 100;
        const milestones = [25, 50, 75, 100];
        milestones.forEach(m => {
          const key = `${w.id}-${m}`;
          if (progress >= m && !prevMilestoneRef.current.has(key)) {
            if (prevMilestoneRef.current.size > 0) {
              fireConfetti();
            }
            prevMilestoneRef.current.add(key);
          }
        });
      }
    });
  }, [allWallets, fireConfetti]);

  const activePlans = paymentPlans.filter(p => p.status === "active" || p.status === "paused");
  const totalPlanRemaining = activePlans.reduce((sum, p) => sum + (p.remainingBalance || 0), 0);
  const nextPayment = activePlans
    .filter(p => !p.isPaused && p.nextPaymentDate)
    .sort((a, b) => new Date(a.nextPaymentDate!).getTime() - new Date(b.nextPaymentDate!).getTime())[0];

  const filteredWallets = searchTerm
    ? allWallets.filter(w =>
        w.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (w.organizationName && w.organizationName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (w.category && w.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
        t(getWalletTypeKey(w.walletType)).toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allWallets;

  if (walletsLoading) {
    return (
      <div className="space-y-4 sm:space-y-6 p-3 sm:p-6">
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const summaryStatsSection = (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="section-summary-stats">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t('walletHub.availableFunds')}</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div ref={balanceCardRef} className="text-xl sm:text-2xl font-bold" data-testid="text-total-balance">
            ${animatedBalance.toFixed(2)}
          </div>
          <p className="text-xs text-muted-foreground">
            Available for healthcare expenses
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t('walletHub.totalContributions')}</CardTitle>
          <TrendingUp className="h-4 w-4 text-green-500" />
        </CardHeader>
        <CardContent>
          <div className="text-xl sm:text-2xl font-bold text-green-600" data-testid="text-total-contributions">
            ${animatedContributions.toFixed(2)}
          </div>
          <p className="text-xs text-muted-foreground">
            {t('walletHub.fromSupportNetwork')}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t('walletHub.totalSpent')}</CardTitle>
          <ArrowDownCircle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-xl sm:text-2xl font-bold" data-testid="text-total-spent">
            ${animatedSpent.toFixed(2)}
          </div>
          <p className="text-xs text-muted-foreground">
            Applied to healthcare expenses
          </p>
        </CardContent>
      </Card>
    </div>
  );

  const mobileBalanceCard = (
    <Card data-testid="mobile-balance-overview">
      <CardContent className="pt-5 pb-4">
        <div className="text-center mb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">{t('walletHub.availableFunds')}</p>
          <div ref={mobileBalanceCardRef} className="text-3xl font-bold" data-testid="text-total-balance-mobile">
            ${animatedBalance.toFixed(2)}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="text-center p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
            <TrendingUp className="h-4 w-4 text-green-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-green-600 dark:text-green-400" data-testid="text-contributions-mobile">
              ${animatedContributions.toFixed(2)}
            </p>
            <p className="text-[10px] text-muted-foreground">{t('walletHub.contributions')}</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-muted/50">
            <ArrowDownCircle className="h-4 w-4 text-muted-foreground mx-auto mb-1" />
            <p className="text-lg font-bold" data-testid="text-spent-mobile">
              ${animatedSpent.toFixed(2)}
            </p>
            <p className="text-[10px] text-muted-foreground">{t('walletHub.spent')}</p>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Link href="/wallet/create" className="flex-1">
            <Button className="w-full" data-testid="button-create-wallet-mobile">
              <Plus className="h-4 w-4 mr-1" />
              {t('walletHub.createWallet')}
            </Button>
          </Link>
          <Link href="/wallet/discover">
            <Button variant="outline" size="icon" data-testid="button-discover-mobile">
              <Compass className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );

  const walletsSection = (
    <Card key="wallets-section" data-testid="section-my-wallets">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>{t('walletHub.myWallets')}</CardTitle>
          <div className="flex items-center gap-2">
            {allWallets.length > 1 && (
              <Badge variant="secondary" data-testid="badge-wallet-count">
                {allWallets.length} wallets
              </Badge>
            )}
          </div>
        </div>
        {allWallets.length > 2 && (
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search wallets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="input-search-wallets"
            />
          </div>
        )}
      </CardHeader>
      <CardContent>
        {allWallets.length === 0 ? (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
              <Wallet className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold mb-2">{t('walletHub.noWalletYet')}</h3>
            <p className="text-muted-foreground mb-4 max-w-sm mx-auto">
              {t('walletHub.noWalletDesc')}
            </p>
            <Link href="/wallet/create">
              <Button data-testid="button-create-first-wallet">
                <Plus className="h-4 w-4 mr-2" />
                {t('walletHub.createFirstWallet')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredWallets.map((wallet) => (
              <Link key={wallet.id} href={`/wallet/${wallet.id}`}>
                <div className="p-4 rounded-lg border hover-elevate cursor-pointer" data-testid={`wallet-card-${wallet.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-primary/10 text-primary">
                        {getWalletTypeIcon(wallet.walletType)}
                      </div>
                      <div>
                        <h3 className="font-semibold">
                          {t('walletHub.walletCard.title', { owner: wallet.organizationName || wallet.patientName, type: t(getWalletTypeKey(wallet.walletType)) })}
                        </h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant={getWalletTypeBadgeVariant(wallet.walletType)}>
                            {t(getWalletTypeKey(wallet.walletType))}
                          </Badge>
                          {wallet.isPublic && (
                            <Badge variant="outline">
                              <Eye className="h-3 w-3 mr-1" />
                              {t('walletHub.walletCard.public')}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold">{formatCents(wallet.balance)}</div>
                      <p className="text-xs text-muted-foreground">{t('walletHub.available')}</p>
                    </div>
                  </div>
                  {wallet.goalAmount && wallet.goalAmount > 0 && (
                    <div className="mt-3">
                      <div className="flex justify-between gap-2 text-sm mb-1">
                        <span className="text-muted-foreground">{t('walletHub.fundingProgress')}</span>
                        <span>{Math.min(100, Math.round((wallet.totalContributions / wallet.goalAmount) * 100))}%</span>
                      </div>
                      <Progress value={Math.min(100, (wallet.totalContributions / wallet.goalAmount) * 100)} className="h-2" />
                    </div>
                  )}
                </div>
              </Link>
            ))}
            {searchTerm && filteredWallets.length === 0 && (
              <p className="text-center text-muted-foreground py-4 text-sm">{t('walletHub.noWalletsMatch')}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const quickActionsSection = (
    <Card data-testid="section-quick-actions">
      <CardHeader>
        <CardTitle>{t('walletHub.quickActions')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Link href="/wallet/discover">
            <div className="flex flex-col items-center justify-center gap-2 p-4 rounded-md border text-center cursor-pointer hover-elevate" data-testid="button-quick-discover">
              <Compass className="h-5 w-5 text-muted-foreground" />
              <span className="text-xs font-medium">{t('walletHub.discover')}</span>
            </div>
          </Link>
          {allWallets.length > 0 && (
            <>
              <Link href={`/wallet/${allWallets[0].id}`}>
                <div className="flex flex-col items-center justify-center gap-2 p-4 rounded-md border text-center cursor-pointer hover-elevate" data-testid="button-quick-share">
                  <Share2 className="h-5 w-5 text-muted-foreground" />
                  <span className="text-xs font-medium">{t('walletHub.shareLink')}</span>
                </div>
              </Link>
              <Link href={`/wallet/${allWallets[0].id}`}>
                <div className="flex flex-col items-center justify-center gap-2 p-4 rounded-md border text-center cursor-pointer hover-elevate" data-testid="button-quick-settings">
                  <Settings className="h-5 w-5 text-muted-foreground" />
                  <span className="text-xs font-medium">{t('walletHub.settings')}</span>
                </div>
              </Link>
            </>
          )}
          <Link href="/community-funds">
            <div className="flex flex-col items-center justify-center gap-2 p-4 rounded-md border text-center cursor-pointer hover-elevate" data-testid="button-quick-community">
              <Heart className="h-5 w-5 text-muted-foreground" />
              <span className="text-xs font-medium">{t('walletHub.communityFunds')}</span>
            </div>
          </Link>
        </div>
      </CardContent>
    </Card>
  );

  const paymentPlansSection = activePlans.length > 0 ? (
    <Card key="payment-plans-section" data-testid="section-payment-plans">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          {t('walletHub.paymentSchedules')}
        </CardTitle>
        <CardDescription>{t('walletHub.activePostCareSchedules')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">{t('walletHub.activePlans')}</p>
              <p className="text-lg font-bold" data-testid="text-active-plans-count">{activePlans.length}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">{t('walletHub.totalRemaining')}</p>
              <p className="text-lg font-bold" data-testid="text-plans-remaining">{formatCents(totalPlanRemaining)}</p>
            </div>
          </div>
          {activePlans.slice(0, 3).map((plan) => (
            <div key={plan.id} className="flex items-center justify-between gap-2 p-3 rounded-lg border" data-testid={`plan-summary-${plan.id}`}>
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                  <FileText className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium">{plan.hospitalName}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatCents(plan.paymentAmount)}/{plan.frequency}
                    {plan.isPaused && " (Paused)"}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">{formatCents(plan.remainingBalance)}</p>
                <p className="text-xs text-muted-foreground">remaining</p>
              </div>
            </div>
          ))}
          {nextPayment && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 text-sm">
              <Clock className="h-4 w-4 text-primary" />
              <span>
                {t('walletHub.nextPaymentLine', {
                  amount: formatCents(nextPayment.paymentAmount),
                  date: format(new Date(nextPayment.nextPaymentDate!), "MMM d, yyyy"),
                })}
              </span>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter>
        <Link href="/payment-plans" className="w-full">
          <Button variant="ghost" className="w-full" data-testid="button-view-payment-plans">
            {t('walletHub.viewAllPaymentSchedules')}
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </Link>
      </CardFooter>
    </Card>
  ) : null;

  const employerSection = (
    <Card key="employer-section" data-testid="section-employer">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          {t('walletHub.employerBenefits')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {employerEnrollment ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-sm font-medium" data-testid="text-employer-name">{employerEnrollment.employer?.companyName || "Your Employer"}</span>
              <Badge variant="default" data-testid="badge-employer-status">{t('walletHub.enrolled')}</Badge>
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">{t('walletHub.totalReceived')}</span>
              <span className="font-medium" data-testid="text-employer-received">{formatCents(employerEnrollment.enrollment?.totalReceivedCents || 0)}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('walletHub.connectEmployerDesc')}</p>
            <Dialog open={isLinkEmployerOpen} onOpenChange={setIsLinkEmployerOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full" data-testid="button-link-employer">
                  <Building2 className="h-4 w-4 mr-2" />
                  {t('walletHub.linkEmployer')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('walletHub.linkEmployerAccount')}</DialogTitle>
                  <DialogDescription>{t('walletHub.linkEmployerHelp')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('walletHub.companyCode')}</label>
                    <Input
                      placeholder="e.g. ACME-2024"
                      value={companyCode}
                      onChange={(e) => setCompanyCode(e.target.value)}
                      data-testid="input-company-code"
                    />
                  </div>
                  <Button
                    className="w-full"
                    onClick={() => linkEmployerMutation.mutate(companyCode)}
                    disabled={!companyCode.trim() || linkEmployerMutation.isPending}
                    data-testid="button-submit-link-employer"
                  >
                    {linkEmployerMutation.isPending ? t('walletHub.linking') : t('walletHub.connectToEmployer')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const recentActivitySection = (
    <Card key="recent-activity-section" data-testid="section-recent-activity">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          {t('walletHub.recentActivity')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {transactionsLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}
          </div>
        ) : recentTransactions.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{t('walletHub.noRecentTransactions')}</p>
          </div>
        ) : (
          <ScrollArea className="h-[300px]">
            <div className="space-y-3">
              {recentTransactions.map((tx) => (
                <div
                  key={tx.id}
                  className="flex items-center justify-between gap-2 p-3 rounded-lg bg-muted/50"
                  data-testid={`transaction-${tx.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-1.5 rounded-full ${
                      tx.type === "contribution" ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400" : "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                    }`}>
                      {tx.type === "contribution" ? (
                        <ArrowUpCircle className="h-4 w-4" />
                      ) : (
                        <ArrowDownCircle className="h-4 w-4" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        {tx.type === "contribution" 
                          ? tx.contributorName || "Anonymous"
                          : tx.description || "Payment"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(tx.createdAt), "MMM d, yyyy")}
                      </p>
                    </div>
                  </div>
                  <div className={`font-medium ${
                    tx.type === "contribution" ? "text-green-600 dark:text-green-400" : ""
                  }`}>
                    {tx.type === "contribution" ? "+" : "-"}{formatCents(Math.abs(tx.amount))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
      {allWallets.length > 0 && recentTransactions.length > 0 && (
        <CardFooter>
          <Link href={`/wallet/${allWallets[0].id}`} className="w-full">
            <Button variant="ghost" className="w-full" data-testid="button-view-all-transactions">
              {t('walletHub.viewAllTransactions')}
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </CardFooter>
      )}
    </Card>
  );

  const emptyPlansSection = activePlans.length === 0 ? (
    <Card data-testid="section-empty-plans">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          Payment Schedules
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center py-4 text-muted-foreground">
          <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{t('walletHub.noActiveSchedules')}</p>
          <p className="text-xs mt-1">{t('walletHub.noActiveSchedulesDesc')}</p>
        </div>
      </CardContent>
      <CardFooter>
        <Link href="/payment-plans" className="w-full">
          <Button variant="ghost" className="w-full" data-testid="button-view-payment-plans-empty">
            {t('walletHub.viewPaymentSchedules')}
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </Link>
      </CardFooter>
    </Card>
  ) : null;

  const conditionFundsSection = conditionMemberships.length > 0 ? (
    <Card key="condition-funds-section" data-testid="section-condition-funds">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HeartPulse className="h-5 w-5" />
          {t('walletHub.joinedCommunityFunds')}
        </CardTitle>
        <CardDescription>{t('walletHub.joinedCommunityFundsDesc')}</CardDescription>
      </CardHeader>
      <CardContent>
        {membershipsLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => <Skeleton key={i} className="h-16" />)}
          </div>
        ) : (
          <div className="space-y-3">
            {conditionMemberships.map((m: any) => (
              <Link key={m.id} href={`/condition-wallets/${m.walletId}`}>
                <div
                  className="flex items-start justify-between gap-3 p-3 rounded-lg border hover-elevate cursor-pointer"
                  data-testid={`condition-membership-${m.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-primary/10 text-primary shrink-0">
                      <HeartPulse className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {m.wallet?.title || "Community Health Fund"}
                      </p>
                      {m.condition?.name && (
                        <p className="text-xs text-muted-foreground">{m.condition.name}</p>
                      )}
                      {m.pledgedMonthlyAmountCents > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          ${(m.pledgedMonthlyAmountCents / 100).toFixed(0)}/month pledged
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0">
                    {m.status === "active" ? (
                      <Badge variant="default" className="text-xs" data-testid={`badge-status-${m.id}`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {t('walletHub.statusActive')}
                      </Badge>
                    ) : m.status === "pending" ? (
                      <Badge variant="secondary" className="text-xs" data-testid={`badge-status-${m.id}`}>
                        <Clock className="w-3 h-3 mr-1" />
                        {t('walletHub.statusPending')}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs" data-testid={`badge-status-${m.id}`}>
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {m.status}
                      </Badge>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Link href="/conditions" className="w-full">
          <Button variant="ghost" className="w-full" data-testid="button-explore-more-funds">
            {t('walletHub.exploreMoreFunds')}
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </Link>
      </CardFooter>
    </Card>
  ) : null;

  const hardshipStatusBadge = (status: string) => {
    if (status === "approved") return <Badge variant="default" data-testid={`badge-hardship-${status}`}>{t('walletHub.statusApproved')}</Badge>;
    if (status === "denied") return <Badge variant="destructive" data-testid={`badge-hardship-${status}`}>{t('walletHub.statusDenied')}</Badge>;
    return <Badge variant="secondary" data-testid={`badge-hardship-${status}`}>{t('walletHub.pendingReview')}</Badge>;
  };

  const hardshipAssistanceSection = (
    <Card key="hardship-section" data-testid="section-hardship-assistance">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <HeartPulse className="h-5 w-5 text-muted-foreground" />
          {t('walletHub.hardshipAssistance')}
        </CardTitle>
        <CardDescription>
          {t('walletHub.hardshipAssistanceDesc')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Dialog open={hardshipDialogOpen} onOpenChange={setHardshipDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              className="w-full"
              data-testid="button-request-hardship-assistance"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('walletHub.submitRequest')}
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="dialog-hardship-assistance">
            <DialogHeader>
              <DialogTitle>{t('walletHub.requestHardship')}</DialogTitle>
              <DialogDescription>
                {t('walletHub.requestHardshipDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label htmlFor="hardship-reason">{t('walletHub.reasonForRequest')}</Label>
                <Textarea
                  id="hardship-reason"
                  placeholder="Describe your hardship situation..."
                  rows={3}
                  value={hardshipForm.reason}
                  onChange={(e) => setHardshipForm(f => ({ ...f, reason: e.target.value }))}
                  data-testid="textarea-hardship-reason"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="hardship-amount">{t('walletHub.amountRequested')}</Label>
                <Input
                  id="hardship-amount"
                  type="number"
                  min="1"
                  step="0.01"
                  placeholder="0.00"
                  value={hardshipForm.amountRequested}
                  onChange={(e) => setHardshipForm(f => ({ ...f, amountRequested: e.target.value }))}
                  data-testid="input-hardship-amount"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="hardship-details">{t('walletHub.additionalDetails')} <span className="text-muted-foreground text-xs">({t('walletHub.optional')})</span></Label>
                <Textarea
                  id="hardship-details"
                  placeholder="Any supporting information, documentation references, or context..."
                  rows={2}
                  value={hardshipForm.supportingDetails}
                  onChange={(e) => setHardshipForm(f => ({ ...f, supportingDetails: e.target.value }))}
                  data-testid="textarea-hardship-details"
                />
              </div>
              {allWallets.length > 0 && (
                <div className="space-y-1.5">
                  <Label htmlFor="hardship-wallet">{t('walletHub.associatedWallet')} <span className="text-muted-foreground text-xs">({t('walletHub.optional')})</span></Label>
                  <select
                    id="hardship-wallet"
                    className="w-full h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    value={hardshipForm.walletId}
                    onChange={(e) => setHardshipForm(f => ({ ...f, walletId: e.target.value }))}
                    data-testid="select-hardship-wallet"
                  >
                    <option value="">{t('walletHub.none')}</option>
                    {allWallets.map(w => (
                      <option key={w.id} value={w.id}>{w.patientName}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setHardshipDialogOpen(false)}
                  data-testid="button-hardship-cancel"
                >
                  {t('walletHub.cancel')}
                </Button>
                <Button
                  onClick={() => submitHardshipMutation.mutate()}
                  disabled={
                    submitHardshipMutation.isPending ||
                    !hardshipForm.reason.trim() ||
                    !hardshipForm.amountRequested ||
                    parseFloat(hardshipForm.amountRequested) <= 0
                  }
                  data-testid="button-hardship-submit"
                >
                  {submitHardshipMutation.isPending ? t('walletHub.submitting') : t('walletHub.submitRequest')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {myHardshipRequests.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('walletHub.yourRequests')}</p>
            <div className="space-y-2">
              {myHardshipRequests.slice(0, 5).map((r: any) => (
                <div
                  key={r.id}
                  className="flex items-start justify-between gap-2 p-2.5 rounded-lg border bg-muted/30"
                  data-testid={`hardship-request-${r.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{r.reason}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatCents(r.amountRequestedCents)} requested
                    </p>
                    {r.adminNote && (
                      <p className="text-xs text-muted-foreground/80 mt-1 italic">{r.adminNote}</p>
                    )}
                  </div>
                  <div className="shrink-0">
                    {hardshipStatusBadge(r.status)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const mobileSlides: React.ReactNode[] = [];
  const mobileLabels: string[] = [];

  mobileSlides.push(walletsSection);
  mobileLabels.push(t('walletHub.myWallets'));

  if (paymentPlansSection) {
    mobileSlides.push(paymentPlansSection);
    mobileLabels.push(t('walletHub.paymentSchedules'));
  }

  if (conditionFundsSection) {
    mobileSlides.push(conditionFundsSection);
    mobileLabels.push(t('walletHub.conditionFunds'));
  }

  mobileSlides.push(employerSection);
  mobileLabels.push(t('walletHub.employer'));

  mobileSlides.push(recentActivitySection);
  mobileLabels.push(t('walletHub.activity'));

  mobileSlides.push(hardshipAssistanceSection);
  mobileLabels.push(t('walletHub.hardshipAssistance'));

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageAbout
        title={t('walletHub.pageAbout.title')}
        description={t('walletHub.pageAbout.description')}
        features={[
          t('walletHub.pageAbout.feature1'),
          t('walletHub.pageAbout.feature2'),
          t('walletHub.pageAbout.feature3'),
          t('walletHub.pageAbout.feature4'),
          t('walletHub.pageAbout.feature5'),
        ]}
        tips={[
          t('walletHub.pageAbout.tip1'),
          t('walletHub.pageAbout.tip2'),
          t('walletHub.pageAbout.tip3'),
        ]}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" data-testid="text-page-title">{t('walletHub.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('walletHub.subtitle')}
          </p>
        </div>
        <div className="hidden sm:flex gap-2">
          <Link href="/wallet/discover">
            <Button variant="outline" data-testid="button-discover-wallets">
              <Compass className="h-4 w-4 mr-2" />
              {t('walletHub.discoverWallets')}
            </Button>
          </Link>
          <Link href="/wallet/create">
            <Button data-testid="button-create-wallet">
              <Plus className="h-4 w-4 mr-2" />
              {t('walletHub.createWallet')}
            </Button>
          </Link>
        </div>
        <div className="sm:hidden flex gap-2" data-testid="mobile-header-ctas">
          <Link href="/wallet/create" className="flex-1">
            <Button className="w-full" data-testid="button-create-wallet-mobile-header">
              <Plus className="h-4 w-4 mr-2" />
              {t('walletHub.createWallet')}
            </Button>
          </Link>
          <Link href="/wallet/discover">
            <Button variant="outline" data-testid="button-discover-wallets-mobile-header">
              <Compass className="h-4 w-4 mr-2" />
              {t('walletHub.discoverWallets')}
            </Button>
          </Link>
        </div>
      </div>

      <OnboardingChecklist />

      <SmartCTAs
        wallets={allWallets}
        transactions={recentTransactions}
        paymentPlans={paymentPlans}
        employerEnrollment={employerEnrollment}
        onLinkEmployer={() => setIsLinkEmployerOpen(true)}
        pendingThankYouCount={pendingThankYouData?.count}
        userCreatedAt={user?.createdAt ?? undefined}
      />

      {pendingInvitations.length > 0 && (
        <Card className="border-primary/50 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              {t('walletHub.pendingInvitations')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingInvitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg border bg-background"
                  data-testid={`invitation-${invitation.id}`}
                >
                  <div>
                    <p className="font-medium">
                      {t('walletHub.invitedToJoin', { name: invitation.inviterName || t('walletHub.someone') })}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('walletHub.role')}: <Badge variant="secondary" className="ml-1">{invitation.role}</Badge>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => acceptInvitationMutation.mutate(invitation.id)}
                      disabled={acceptInvitationMutation.isPending}
                      data-testid={`button-accept-${invitation.id}`}
                    >
                      {t('walletHub.accept')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => declineInvitationMutation.mutate(invitation.id)}
                      disabled={declineInvitationMutation.isPending}
                      data-testid={`button-decline-${invitation.id}`}
                    >
                      {t('walletHub.decline')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <ConfettiCanvas />

      <div className="block md:hidden space-y-4" data-testid="mobile-layout">
        {mobileBalanceCard}

        <MobileCardDeck sectionLabels={mobileLabels}>
          {mobileSlides}
        </MobileCardDeck>

        {quickActionsSection}

        {emptyPlansSection}

        <ReceivedThankYous />

        <CommunityImpactSection variant="hub" />
      </div>

      <div className="hidden md:block space-y-6" data-testid="desktop-layout">
        {summaryStatsSection}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {walletsSection}
            {conditionFundsSection}
            {quickActionsSection}
            {paymentPlansSection}
            {employerSection}
          </div>

          <div className="space-y-6">
            {recentActivitySection}
            {hardshipAssistanceSection}
            {emptyPlansSection}
            <ReceivedThankYous />
            <CommunityImpactSection variant="hub" />
          </div>
        </div>
      </div>
    </div>
  );
}
